import React, { useEffect, useState } from 'react';
import { Portal } from '@withjoy/joykit';
import { useTour } from '../context/TourContext';
import type { StepHandle } from '../context/TourContext';
import { TourOverlay } from './TourOverlay';
import { TourBackdrop } from './TourBackdrop';
import { useScrollToElement } from '@shared/utils/hooks/useScrollToElement';
import { addError } from '@shared/utils/logger';
import { withWindow } from '@shared/utils/withWindow';

/**
 * Checks if an element is scrollable.
 * @param element - The element to check.
 * @param global - The global window object.
 * @returns True if the element is scrollable, false otherwise.
 */
const isScrollable = (element: HTMLElement, global: Window): boolean => {
  // 1. cheap: actual overflow
  const hasVerticalOverflow = element.scrollHeight > element.clientHeight;
  const hasHorizontalOverflow = element.scrollWidth > element.clientWidth;

  if (!hasVerticalOverflow && !hasHorizontalOverflow) {
    return false;
  }

  // 2. expensive: computed style only when needed
  const style = global.getComputedStyle(element);
  const allowsScroll = (overflow: string) => {
    const normalized = overflow.toLowerCase();
    return ['auto', 'scroll', 'overlay'].includes(normalized);
  };

  const canScrollVertically = hasVerticalOverflow && allowsScroll(style.overflowY);
  const canScrollHorizontally = hasHorizontalOverflow && allowsScroll(style.overflowX);

  return canScrollVertically || canScrollHorizontally;
};

/**
 * Finds the closest scrollable ancestor of an element.
 * @param node - The element to find the closest scrollable ancestor of.
 * @param options - Controls cache usage and refresh behavior.
 * @returns The closest scrollable ancestor element, or null if no scrollable ancestor is found.
 */
const findClosestScrollableAncestor = (node: HTMLElement | null): HTMLElement | null =>
  withWindow(global => {
    if (!node) {
      return null;
    }

    let current = node.parentElement ?? null;
    let result: HTMLElement | null = null;

    while (current) {
      if (isScrollable(current, global)) {
        result = current;
        break;
      }
      current = current.parentElement;
    }

    return result;
  }, null);

/**
 * Displays the active tour step with backdrop and overlay.
 * Handles step state, rect tracking, and scrolling coordination.
 */
export const TourStepDisplay: React.FC = () => {
  const tour = useTour();
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [currentStep, setCurrentStep] = useState<StepHandle | null>(null);

  // Extract stable values from context
  const isOpen = tour.state.stateProps.isOpen;
  const currentStepName = tour.state.stateProps.currentStepName;
  const steps = tour.state.steps;

  const [scrollContainer, setScrollContainer] = useState<HTMLElement | null>(null);

  // Resolve the closest scrollable ancestor for the active step; fall back to window when none exists
  useEffect(() => {
    setScrollContainer(findClosestScrollableAncestor(currentStep?.node ?? null));
  }, [currentStep?.node]);

  const { isScrolling } = useScrollToElement(currentStep?.node, undefined, scrollContainer);

  // Update current step when active step changes
  useEffect(() => {
    if (!isOpen || !currentStepName || !steps) {
      setCurrentStep(null);
      setTargetRect(null);
      return;
    }

    const step = steps.get(currentStepName);
    if (!step) {
      addError(new Error(`[Tour] Step not registered: ${currentStepName}`), { context: 'TourStepDisplay' });
      setCurrentStep(null);
      setTargetRect(null);
      return;
    }

    setCurrentStep(step);
  }, [isOpen, currentStepName, steps]);

  // Track target rect with observer
  useEffect(() => {
    if (!currentStep || !isOpen || isScrolling) {
      setTargetRect(null);
      return;
    }

    const updateRect = () => setTargetRect(currentStep.getRect());

    // Get initial rect
    updateRect();

    // Subscribe to rect updates
    const unsubscribe = currentStep.observe(updateRect);

    return unsubscribe;
  }, [currentStep, isOpen, isScrolling]);

  // Don't render Portal if tour is not open
  if (!isOpen) {
    return null;
  }

  const isStepReadyToRender = currentStepName && targetRect && currentStep && !isScrolling;

  return (
    <Portal>
      <TourBackdrop />
      {isStepReadyToRender && (
        <TourOverlay
          targetRect={targetRect}
          targetElement={currentStep.node}
        >
          {currentStep.tooltip}
        </TourOverlay>
      )}
    </Portal>
  );
};

TourStepDisplay.displayName = 'TourStepDisplay';
